import React, { useState, useEffect, useMemo, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { 
  Box, 
  Container,
  Paper,
  Typography,
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Button, 
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  Pagination,
  SelectChangeEvent,
  TableContainer,
  useMediaQuery,
  useTheme,
  Grid,
  IconButton,
  Badge,
  Tooltip,
  Menu,
  MenuItem as MuiMenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input
} from '@mui/material';
import { 
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  MoreVert as MoreVertIcon,
  Upload as UploadIcon,
  FileUpload as FileUploadIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import userService, { User, UserFilter } from '../../services/userService';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { AuthUtils } from '../../utils/AuthUtils';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import config from '../../config';
import { UserRole } from '../../types/user';

// ユーザー一覧ページで使用する拡張ユーザー型
interface ExtendedUser extends User {
}

const USER_ROLE_LABELS: Record<string, string> = {
  'ADMIN': '管理者',
  'MANAGER': 'マネジャー',
  'STAFF': '内勤スタッフ',
  'DRIVER': 'ドライバー',
  'CAST': 'キャスト',
  'CUSTOMER': '顧客'
};

const USER_ROLE_COLORS: Record<string, "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"> = {
  'ADMIN': 'error',
  'MANAGER': 'warning',
  'STAFF': 'info',
  'DRIVER': 'success',
  'CAST': 'primary',
  'CUSTOMER': 'secondary'
};

// 役割の正規化関数（大文字に変換）
const normalizeRole = (role: string): string => {
  if (!role) return '';
  return role.toUpperCase();
};

// 役割名と色のマッピングを修正して、大文字小文字を区別しないようにする
const getRoleLabel = (role: string): string => {
  const normalizedRole = normalizeRole(role);
  return USER_ROLE_LABELS[normalizedRole as keyof typeof USER_ROLE_LABELS] || normalizedRole;
};

const getRoleColor = (role: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  const normalizedRole = normalizeRole(role);
  return USER_ROLE_COLORS[normalizedRole as keyof typeof USER_ROLE_COLORS] || 'default';
};

const CustomerListPage: React.FC = () => {
  const [users, setUsers] = useState<ExtendedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);
  const [filter, setFilter] = useState<{
    status?: string;
  }>({
    status: undefined
  });
  const [searchTerm, setSearchTerm] = useState('');            // 入力欄の即時表示用
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(''); // 検索実行用（遅延反映）
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // APIリクエスト用のフィルターを作成
      const filterParams: UserFilter = {
        role: 'CUSTOMER' // Always filter for customers
      };
      
      // ステータスのフィルタリング
      if (filter.status && filter.status !== '') {
        filterParams.status = filter.status;
      }
      
      // 検索キーワードのフィルタリング（遅延反映された値を使用）
      if (debouncedSearchTerm && debouncedSearchTerm.trim() !== '') {
        filterParams.search = debouncedSearchTerm.trim();
      }

      // ユーザー情報取得
      const response = await userService.getUsers(
        page, 
        pageSize, 
        filterParams
      );

      setUsers(response.users);
      setTotalUsers(response.total);
      setLoading(false);
    } catch (err) {
      console.error('顧客一覧の取得エラー:', err);
      const errorMessage = err instanceof Error ? err.message : '予期せぬエラーが発生しました';
      setError(errorMessage);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();

    const loggedInUser = AuthUtils.getStoredUser();
    if (loggedInUser) {
      const userWithStatus = {
        ...loggedInUser,
        status: 'active' 
      };
      setCurrentUser(userWithStatus as User);
    }
  }, [page, filter, debouncedSearchTerm]);

  // 入力を500ms遅延させて検索実行用の値へ反映する（オーダー作成の電話番号検索と同様）
  const applyDebouncedSearch = useCallback(
    debounce((value: string) => {
      setDebouncedSearchTerm(value);
    }, 500),
    []
  );

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);           // 入力欄は即時更新
    applyDebouncedSearch(value);    // 検索実行は遅延
  };

  // アンマウント時に保留中のdebounceをキャンセル
  useEffect(() => {
    return () => {
      applyDebouncedSearch.cancel();
    };
  }, [applyDebouncedSearch]);

  const handleCreateUser = () => {
    navigate('/users/create');
  };

  const handleEditUser = (userId: number) => {
    navigate(`/users/edit/${userId}`);
  };

  const handleToggleStatus = async (userId: number) => {
    try {
      const updatedUser = await userService.toggleUserStatus(userId);
      await fetchUsers();
      setSnackbar({
        open: true,
        message: `ユーザーステータスを${updatedUser.is_active ? '有効' : '無効'}に変更しました`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'ステータスの切り替えに失敗しました',
        severity: 'error'
      });
    }
  };

  const handleDeleteUser = async (user: User) => {
    try {
      await userService.deleteUser(user.id);
      setUsers(users.filter(u => u.id !== user.id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '予期せぬエラーが発生しました';
      setError(errorMessage);
    }
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const status = event.target.value;
    setFilter(prev => ({
      ...prev,
      status: status === '' ? undefined : status
    }));
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };
  
  const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>, user: User) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedUser(null);
  };

  const handleActionSelect = (value: string, user: User) => {
    if (value === 'edit') {
      handleEditUser(user.id);
    }
  };

  const isAdmin = useMemo(() => {
    return currentUser?.role?.toUpperCase() === 'ADMIN';
  }, [currentUser]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setCsvFile(event.target.files[0]);
    }
  };

  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setCsvFile(null);
  };

  const handleImportCsv = async () => {
    if (!csvFile) {
      enqueueSnackbar('CSVファイルを選択してください', { variant: 'warning' });
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const response = await userService.importCsv(csvFile);
      enqueueSnackbar(response.message || '顧客のインポートに成功しました', { variant: 'success' });
      handleCloseImportDialog();
      fetchUsers(); 
    } catch (err: any) {
        console.error('CSVインポートエラー:', err);
        let errorMessage = '顧客のインポートに失敗しました。';
        if (axios.isAxiosError(err) && err.response) {
            const errorData = err.response.data;
            if (errorData.error) {
                errorMessage = errorData.error;
                if (errorData.details) {
                    errorMessage += `: ${errorData.details}`;
                }
            } else if (errorData.message) {
                errorMessage = errorData.message;
            }
        } else if (err instanceof Error) {
            errorMessage = err.message;
        }
        setError(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
    } finally {
      setImporting(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const blob = await userService.exportCsv({ role: 'CUSTOMER' });
      const now = new Date();
      const dateStr = format(now, 'yyyyMMdd_HHmmss');
      const fileName = `customers_export_${dateStr}.csv`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('CSVエクスポートが完了しました', { variant: 'success' });
    } catch (error) {
      console.error('CSVエクスポートに失敗しました:', error);
      enqueueSnackbar('CSVエクスポートに失敗しました', { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="顧客管理">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress />
      </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title="顧客管理">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '70vh' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchUsers}>
          再読み込み
        </Button>
      </Box>
      </DashboardLayout>
    );
  }

  const renderImportDialog = () => (
    <Dialog open={importDialogOpen} onClose={handleCloseImportDialog}>
      <DialogTitle>顧客CSVインポート</DialogTitle>
      <DialogContent>
        <Box sx={{ my: 2 }}>
          <Typography variant="body1" gutterBottom>
            顧客情報をCSVファイルからインポートします。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            必須フィールド: ユーザー名, メールアドレス, 役割(CUSTOMER固定)
          </Typography>
          <Input
            type="file"
            inputProps={{ accept: '.csv' }}
            onChange={handleFileChange}
            fullWidth
            disabled={importing}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseImportDialog} disabled={importing}>
          キャンセル
        </Button>
        <Button 
          onClick={handleImportCsv} 
          color="primary" 
          disabled={!csvFile || importing}
          startIcon={importing ? <CircularProgress size={20} /> : <FileUploadIcon />}
        >
          {importing ? 'インポート中...' : 'インポート'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <DashboardLayout title="顧客管理">
    <Container maxWidth="lg">
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h1" fontWeight="bold">
              顧客管理
            </Typography>
          </Box>
          
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={8} md={9}>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={4}>
            <TextField 
                    placeholder="名前・メール・電話番号で検索" 
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
                    fullWidth
                    size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />
              }}
            />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <Box sx={{ display: 'flex', gap: 1, mt: { xs: 1, sm: 0 } }}>
                    {isAdmin && (
                      <>
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<UploadIcon />}
                          onClick={handleOpenImportDialog}
                          size={isMobile ? "small" : "medium"}
                        >
                          CSV取込
                        </Button>
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
                          onClick={handleExportCsv}
                          disabled={exporting || loading}
                          size={isMobile ? "small" : "medium"}
                        >
                          CSVエクスポート
                        </Button>
                      </>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <FormControl fullWidth size={isMobile ? "small" : "medium"}>
              <InputLabel>ステータス</InputLabel>
              <Select 
                value={filter.status || ''}
                onChange={handleStatusChange}
                label="ステータス"
              >
                      <MenuItem value="">すべて</MenuItem>
                      <MenuItem value="active">有効</MenuItem>
                      <MenuItem value="inactive">無効</MenuItem>
              </Select>
            </FormControl>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={4} md={3} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 1 }}>
          <Button 
            variant="contained" 
            color="primary" 
                startIcon={!isMobile && <AddIcon />}
            onClick={handleCreateUser}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
          >
                {isMobile ? "新規作成" : "新規顧客作成"}
          </Button>
            </Grid>
          </Grid>

        <Paper elevation={3}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                    {!isMobile && <TableCell>ID</TableCell>}
                    <TableCell>名前</TableCell>
                    {!isMobile && <TableCell>電話番号</TableCell>}
                <TableCell>役割</TableCell>
                    <TableCell align="right">アクション</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                  {users.length > 0 ? (
                    users.map(user => (
                <TableRow key={user.id}>
                        {!isMobile && <TableCell>{user.id}</TableCell>}
                  <TableCell>
                          {user.first_name || user.username}
                          {isMobile && <Box mt={0.5} sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{user.phone_number || '-'}</Box>}
                  </TableCell>
                        {!isMobile && <TableCell>{user.phone_number || '-'}</TableCell>}
                  <TableCell>
                    <Chip 
                            label={getRoleLabel(user.role)}
                            color={getRoleColor(user.role)}
                            size={isMobile ? "small" : "medium"}
                    />
                  </TableCell>
                        <TableCell align="right">
                      <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => navigate(`/customers/${user.id}/orders`)}
                      >
                        履歴
                      </Button>
                      <Button 
                            variant="contained"
                            color="primary"
                        size="small" 
                        onClick={() => handleEditUser(user.id)}
                      >
                        編集
                      </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 4 : 5} align="center">
                        顧客が見つかりません
                  </TableCell>
                </TableRow>
                  )}
            </TableBody>
          </Table>
            </TableContainer>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(totalUsers / pageSize)}
            page={page}
            onChange={handlePageChange}
            color="primary"
              size={isMobile ? "small" : "medium"}
          />
        </Box>
      </Box>
    </Container>
      
      {renderImportDialog()}
    </DashboardLayout>
  );
};

export default CustomerListPage; 